import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, GraphNodeSubdomainEnum } from "~/types/enums";
import { secured } from "react-abac";
import RestrictedSection from "~/components/RestrictedSection";
import CoreContext from "~/lib/context";
import { Alert, Box, Modal, Spinner, Text } from "~/components-shared";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import KnowledgeGraphControlCard from "~/components/KnowledgeGraphControlCard";
import api from "~/lib/api";
import { handleError } from "~/lib/helpers";
import GraphNodeDetails from "~/components/GraphNodeDetails";
import KnowledgeGraphCanvas from "~/views/private/KnowledgeGraphCanvas";
import KnowledgeGraphCreateNodeModal from "~/views/private/KnowledgeGraphCreateNodeModal";
import KnowledgeGraphDeleteNodeModal from "~/views/private/KnowledgeGraphDeleteNodeModal";
const StyledBox = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
`;
const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;
const Wrapper = styled.div`
  width: 100%;
  height: 100vh;
  background-color: transparent;
  position: relative;
`;
const StyledKnowledgeGraphControlCard = styled(KnowledgeGraphControlCard)`
  backdrop-filter: blur(5px);
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}}95;
`;
const KnowledgeGraph = () => {
  const {
    t
  } = useTranslation();
  const {
    setSidebar
  } = useContext(CoreContext);
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const [selectedNode, setSelectedNode] = useState(null);
  const [createdNode, setCreatedNode] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [minEdges, setMinEdges] = useState(0);
  const [maxEdges, setMaxEdges] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  const loadData = () => {
    setLoading(true);
    setError(undefined);
    setTimeout(() => {
      api.fetchGraphData().then(data => {
        setNodes(data.nodes);
        setEdges(data.edges);
        setMinEdges(data.minConnections);
        setMaxEdges(data.maxConnections);
      }).catch(e => setError(handleError(e))).finally(() => setLoading(false));
    }, 500);
  };
  useEffect(() => {
    loadData();
  }, []);
  const handleCreated = node => {
    setNodes([...nodes, node]);
    setCreatedNode(node);
    setSelectedNode(node);
  };
  const handleDeleted = nodeId => {
    setSelectedNode(null);
    setNodes(nodes.filter(node => node.id !== nodeId));
    setEdges(edges.filter(edge => edge.targetId !== nodeId || edge.sourceId !== nodeId));
  };
  const filteredNodes = useMemo(() => nodes.map(node => {
    switch (selectedFilter) {
      case GraphNodeSubdomainEnum.CUSTOMER:
        return {
          ...node,
          selected: node.subdomain === selectedFilter
        };
      case GraphNodeSubdomainEnum.PRODUCT:
        return {
          ...node,
          selected: node.subdomain === selectedFilter
        };
      case "PERSON":
        return {
          ...node,
          selected: node.data.subtype === selectedFilter
        };
      case "ORGANIZATION":
        return {
          ...node,
          selected: node.data.subtype === selectedFilter
        };
      default:
        return node;
    }
  }), [nodes, selectedFilter]);
  const handleNodeCreate = useCallback(() => {
    setCreateModalOpen(true);
  }, []);
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(StyledBox, {
    direction: "column",
    s: {
      pt: 12,
      pl: 14
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Text, {
    style: {
      textShadow: "2px 2px black"
    },
    element: "h1",
    s: {
      mb: 3
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("KnowledgeGraph.knowledgeGraph")), /*#__PURE__*/React.createElement(StyledKnowledgeGraphControlCard, {
    color: "transparent",
    selectedFilter: selectedFilter,
    onFilterChange: setSelectedFilter,
    s: {
      p: 2
    },
    shadow: true
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mt: 3
    },
    fullWidth: true
  }, error)), loading ? /*#__PURE__*/React.createElement(StyledSpinner, {
    color: "primary",
    scale: 15
  }) : !!nodes.length && /*#__PURE__*/React.createElement(KnowledgeGraphCanvas, {
    nodes: filteredNodes,
    edges: edges,
    createdNode: createdNode,
    onNodeSelect: setSelectedNode,
    onNodeCreate: handleNodeCreate
  }), /*#__PURE__*/React.createElement(GraphNodeDetails, {
    node: selectedNode,
    minEdges: minEdges,
    maxEdges: maxEdges,
    onNodeDelete: setDeleteModal,
    onNodeSelect: setSelectedNode,
    onClose: () => setSelectedNode(null)
  }), /*#__PURE__*/React.createElement(Modal, {
    open: createModalOpen
  }, /*#__PURE__*/React.createElement(KnowledgeGraphCreateNodeModal, {
    onClose: () => setCreateModalOpen(false),
    onCreated: handleCreated
  })), /*#__PURE__*/React.createElement(Modal, {
    open: !!deleteModal
  }, /*#__PURE__*/React.createElement(KnowledgeGraphDeleteNodeModal, {
    onClose: () => setDeleteModal(null),
    node: deleteModal,
    onDeleted: handleDeleted
  })));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.TRENDS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(KnowledgeGraph), () => true);